import { Link, useMatches, useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clapperboard, House, User, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { useLocalBookings } from "../../features/account/lib/local-bookings";
import { signOut, useSession } from "../../lib/auth";
import {
  AccountModal,
  type TabId as AccountTabId,
} from "../../features/account/components/account-modal";
import { useMenu, useMenuGroup, usePresence } from "../ui/use-presence";
import { BottomBarAccountMenu } from "./bottom-bar-account-menu";
import { BottomBarPlus } from "./bottom-bar-plus";
import { activeLabelFits } from "./bottom-bar-fit";
import { decideShrink, SHRINK_THRESHOLD, SHRINK_TOP_ZONE } from "./bottom-bar-shrink";
import { SOFT_SURFACE, SOFT_WELL, SOFT_WELL_FILL } from "./soft-bar";

const NAVY = "#110E39";
const GOLD = "#E5B265";

type Tab = {
  key: string;
  label: string;
  icon: LucideIcon;
  /** A tab either goes somewhere, or opens the account panel above the bar. */
  href?: string;
  badge?: number;
  match: (pathname: string, locale: string) => boolean;
};

const APPLICATION_ROUTES = new Set<string>([
  "/$lang/_layout/creators",
  "/$lang/_layout/ambassadors",
  "/$lang/_layout/become-a-partner",
]);

/** Pages that carry Discover's own phone bar (`DiscoverRail`). */
const DISCOVER_RAIL_PATH = /^\/[^/]+\/(discover|trips|ai-chat|liked)(\/|$)/;

/** useLayoutEffect in the browser (measure before paint), a no-op on the server. */
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export function MobileBottomBar() {
  const { t } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const matches = useMatches();
  const { items: localBookings } = useLocalBookings();
  const activeBookings = localBookings.filter((b) => b.status !== "cancelled" && b.status !== "completed").length;

  const navigate = useNavigate();
  const { data: session } = useSession();
  const isAuthed = Boolean(session?.user);
  // The account panel and the + menu share one group: one bar, one thing open.
  const menus = useMenuGroup();
  const menu = useMenu<HTMLDivElement>(menus, "bottom-account");
  const plus = useMenu<HTMLDivElement>(menus, "bottom-plus");
  const presence = usePresence(menu.open);
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const [accountTab, setAccountTab] = useState<AccountTabId | null>(null);

  /*
   * The lit tab shows its name only when the whole name fits. On a 320px
   * phone, or with a long name ("Startseite"), it keeps the icon in the well
   * instead, so the other tabs never slide under the + (see bottom-bar-fit.ts).
   * The name stays in the DOM, visually hidden, so it can be measured again
   * when the screen turns or the language changes.
   */
  const pillRef = useRef<HTMLDivElement>(null);
  const [labelFits, setLabelFits] = useState(true);
  useIsoLayoutEffect(() => {
    const pill = pillRef.current;
    if (!pill) return;
    const measure = () => {
      const label = pill.querySelector<HTMLElement>("[data-bar-label]");
      if (!label) return;
      const count = pill.querySelectorAll("[data-bar-tab]").length;
      setLabelFits(activeLabelFits(pill.clientWidth, label.scrollWidth, count));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(pill);
    return () => ro.disconnect();
  });

  /*
   * The bar steps back as you scroll down, like the app's (owner, 2026-10-18):
   * it shrinks to the app's compact size, labels and all, instead of sliding
   * off the screen. Scrolling up, or reaching the top, brings it back. When
   * is decided by `decideShrink`, the app's own rule; how it moves is in
   * tailwind.css (.mj-bottom-bar).
   */
  const [scrolledDown, setScrolledDown] = useState(false);
  useEffect(() => {
    let anchor = window.scrollY;
    let travel = 0;
    let compactNow = false;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        // No room to scroll means no bottom edge to bounce on.
        const next = decideShrink(y, y - anchor, compactNow, travel, maxY > 0 ? maxY : undefined);
        travel = next.travel;
        if (next.target !== null) {
          compactNow = next.target;
          setScrolledDown(next.target);
        }
        // A slow drag moves less than the threshold per frame. Keep measuring
        // from the same point until it has moved enough to count, so a slow
        // scroll still adds up. Inside the bottom bounce, measure from the edge.
        if (Math.abs(y - anchor) >= SHRINK_THRESHOLD || y <= SHRINK_TOP_ZONE) {
          anchor = maxY > 0 && y > maxY ? maxY : y;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  // An open menu is a deliberate stop, so the bar stays full size under it
  // (the app does the same).
  const compact = scrolledDown && !menu.open && !plus.open;
  // Published on <html> so things floating above the bar (the hubs' Filters
  // and Map pill) can follow its top down instead of hanging mid-screen.
  useEffect(() => {
    const root = document.documentElement;
    if (compact) root.dataset.bottomBarCompact = "";
    else delete root.dataset.bottomBarCompact;
    return () => {
      delete root.dataset.bottomBarCompact;
    };
  }, [compact]);

  /* Shut the menus when the session changes underneath them. */
  const { close } = menu;
  const { close: closePlus } = plus;
  useEffect(() => {
    close();
    closePlus();
  }, [isAuthed, close, closePlus]);
  // A tap on another tab is inside the menu's box, so it never reads as an
  // outside press: the panel stayed open over the page it navigated to.
  useEffect(() => {
    close();
    closePlus();
  }, [pathname, close, closePlus]);

  const onListingFlow = matches.some((m) =>
    [
      "/$lang/_layout/$slug",
      "/$lang/_layout/$slug/packages",
      "/$lang/_layout/$slug/checkout",
      "/$lang/_layout/$city/$category/$slug",
      "/$lang/_layout/$city/$category/$slug/",
      "/$lang/_layout/$city/$category/$slug/packages",
      "/$lang/_layout/stays/$city/$slug/",
      // The hotel checkout: the bar covered the add-on sheet's own button.
      "/$lang/_layout/stays/checkout",
      "/$lang/_layout/bucket",
      "/$lang/_layout/bucket/checkout",
      "/$lang/_layout/booking/$bookingId/thank-you",
      // A ride page pins its own "Request ride" bar to the bottom. (The hub
      // books in a sheet, so it keeps the site's bar.)
      "/$lang/_layout/transport_/$slug",
    ].includes(m.routeId as string),
  );
  if (onListingFlow) return null;
  // The application forms (creators, ambassadors, partners) get the whole
  // phone screen: the bar covered the form's own buttons (owner, 2026-10-09).
  if (matches.some((m) => APPLICATION_ROUTES.has(m.routeId as string))) return null;
  // Discover and the three tabs of its rail (trips, MaJourneys AI, liked)
  // bring their own bottom bar on the phone. Two bars stacked into one doubled
  // frame when those tabs moved off /discover onto their own URLs.
  if (DISCOVER_RAIL_PATH.test(pathname)) return null;
  // Thank-you confirmation page — keep focus on the receipt + create-account
  // CTA. Path: /:lang/booking/:bookingId/thank-you (covers any routeId variant).
  if (pathname.match(/^\/[^/]+\/booking\/[^/]+\/thank-you\/?$/)) return null;

  const tabs: readonly Tab[] = [
    {
      key: "home",
      label: t("nav.home"),
      icon: House,
      href: `/${locale}`,
      match: (p, l) => p === `/${l}` || p === `/${l}/`,
    },
    {
      key: "videos",
      label: t("nav.videos"),
      icon: Clapperboard,
      href: `/${locale}/reels`,
      match: (p, l) => p === `/${l}/reels` || p.startsWith(`/${l}/reels/`),
    },
    {
      key: "booking",
      label: t("nav.booking"),
      icon: Calendar,
      href: `/${locale}/bookings`,
      badge: activeBookings,
      match: (p, l) =>
        p.startsWith(`/${l}/bookings`) ||
        p.startsWith(`/account/reservations`) ||
        p.startsWith(`/account/bookings`),
    },
    {
      key: "account",
      label: t("nav.account"),
      icon: User,
      // Opens the panel above the bar instead of routing. Bucket lives in
      // there too, so guests get the panel as well (Bucket + Sign in).
      match: (p, l) => p.startsWith(`/${l}/bucket`) || p === "/account" || (p.startsWith("/account") && !p.startsWith("/account/reservations") && !p.startsWith("/account/bookings")),
    },
  ];

  function go(href: string) {
    menu.close();
    navigate({ href });
  }

  /** The + menu's pages keep what the guest chose (city, dates, guests…). */
  function goKeepingSearch(path: string) {
    plus.close();
    navigate({ href: `${path}${searchStr}` });
  }

  function pick(tab: AccountTabId) {
    menu.close();
    setAccountTab(tab);
  }

  async function handleSignOut() {
    menu.close();
    await signOut();
    navigate({ href: "/sign-in" });
  }

  return (
    <nav
      aria-label={t("common.primary_navigation")}
      data-compact={compact ? "true" : "false"}
      className="mj-bottom-bar fixed inset-x-0 bottom-0 z-[var(--z-bottom-bar)] flex justify-center px-4 pb-[max(12px,env(safe-area-inset-bottom))] lg:hidden"
    >
      <div ref={menu.ref} className="relative flex w-full max-w-[380px] items-center gap-2">
        {presence.mounted ? (
          <BottomBarAccountMenu
            state={presence.state}
            panelRef={presence.ref}
            isAuthed={isAuthed}
            onPick={pick}
            onNavigate={go}
            onSignOut={handleSignOut}
          />
        ) : null}

        <div
          ref={pillRef}
          className="flex h-14 min-w-0 flex-1 items-center gap-0.5 rounded-full p-1.5"
          style={SOFT_SURFACE}
        >
          {tabs.map((tab) => {
            // While a menu is open, its own control alone is lit: the page's
            // own tab dims, so two things never read as selected (owner, 2026-10-14).
            const active = menu.open
              ? tab.key === "account"
              : plus.open
                ? false
                : tab.match(pathname, locale);
            const TabIcon = tab.icon;
            const hasBadge = Boolean(tab.badge && tab.badge > 0);
            const label = hasBadge
              ? t("nav.booking_with_count", { label: tab.label, count: tab.badge })
              : tab.label;
            // The well carries the name; the other tabs are icons alone, with
            // the name kept for screen readers. The lit tab may shrink (to 44px,
            // never less) so it can not push its neighbours under the +.
            const className = cn(
              "mj-press relative flex h-11 min-w-[44px] items-center justify-center rounded-full text-[13px] font-semibold leading-none",
              active ? (labelFits ? "shrink gap-1.5 overflow-hidden px-4" : "shrink px-3") : "flex-1",
            );
            const inner = (
              <>
                <span
                  className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center"
                  // The booking ticket's stub flies here once kept.
                  data-account-anchor={tab.key === "account" ? "" : undefined}
                >
                  <TabIcon size={20} strokeWidth={2.1} aria-hidden />
                  {hasBadge ? (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-2 -end-2.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none tabular-nums"
                      style={{ background: GOLD, color: NAVY, boxShadow: `0 0 0 2px ${active ? SOFT_WELL_FILL : "#F6F6F9"}` }}
                    >
                      {(tab.badge ?? 0) > 9 ? "9+" : tab.badge}
                    </span>
                  ) : null}
                </span>
                {active ? (
                  <span data-bar-label="" className={labelFits ? "min-w-0 overflow-hidden whitespace-nowrap" : "sr-only"}>
                    {tab.label}
                  </span>
                ) : null}
              </>
            );
            const style: CSSProperties = { color: NAVY, ...(active ? SOFT_WELL : null) };
            const ariaLabel = active && !hasBadge ? undefined : label;

            if (!tab.href) {
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={menu.toggle}
                  aria-haspopup="menu"
                  aria-expanded={menu.open}
                  aria-label={ariaLabel}
                  data-bar-tab=""
                  className={className}
                  style={style}
                >
                  {inner}
                </button>
              );
            }

            return (
              <Link
                key={tab.key}
                to={tab.href}
                // Keep what the guest chose (city, dates, guests…) on the way
                // to another tab and back.
                search={(prev: Record<string, unknown>) => prev}
                // Also covers tapping the tab you are already on.
                onClick={() => {
                  close();
                  closePlus();
                }}
                aria-current={active ? "page" : undefined}
                aria-label={ariaLabel}
                data-bar-tab=""
                className={className}
                style={style}
              >
                {inner}
              </Link>
            );
          })}
        </div>

        <BottomBarPlus menu={plus} locale={locale} onNavigate={goKeepingSearch} />
      </div>

      {accountTab ? (
        <AccountModal initialTab={accountTab} onClose={() => setAccountTab(null)} />
      ) : null}
    </nav>
  );
}
